import { Container } from "pixi.js";
import { Persistence } from "../config/constants";
import { BaseEntity } from "../core/entity/BaseEntity";
import { Entity } from "../core/entity/Entity";
import { on } from "../core/entity/handler";
import {
  InteractAction,
  InteractFacts,
  resolveInteract,
} from "../game/interact";
import { atTruckBed } from "../game/lot";
import { hasFloorControls } from "../game/Machine";
import { atStand, isSellable } from "../game/stand";
import { ShellStore } from "../shell/ShellStore";
import { TargetingState } from "../shell/dispatch/TargetingState";
import {
  findMachineEntity,
  shopCellMap,
} from "../sim/commands/machine-commands";
import { MachineView } from "./MachineView";
import { TripTheater } from "../shell/scenes/TripTheater";
import { MaterialPileEntity } from "../sim/entities/MaterialPileEntity";
import { Player } from "../sim/entities/Player";
import { StandEntity } from "../sim/entities/StandEntity";
import { TruckEntity } from "../sim/entities/TruckEntity";
import { interactFacts } from "../sim/commands/interact-commands";
import { StandView } from "./StandView";
import {
  findChildView,
  TARGET_HIGHLIGHT_FILTERS,
  viewHighlightRoot,
} from "./targetHighlight";
import { TruckView } from "./TruckView";

/**
 * The in-world targeting treatment — the old targetHighlight.ts filters
 * applied by an entity instead of React props. Everything the keys are
 * about to act on wears the same white rim, so "this is the target"
 * reads the same for a station, a board on the floor, the for-sale
 * stand, and the truck.
 *
 * What each of those means is decided here rather than in the views: the
 * rim comes off the same resolver the keyboard uses (with the cursor's
 * position on E's ring applied — set by the mouse hovering, or by R),
 * so the outline and the keypress can never disagree about which piece
 * leaves the floor, the bench, the bed, or the table, or whether E
 * leans over the bench instead.
 */
export class TargetHighlightView extends BaseEntity implements Entity {
  persistenceLevel: number = Persistence.Permanent;
  pausable = false;

  /** How to put back what was dressed last frame. */
  private undressers: Array<() => void> = [];
  /** The version the rims were last resolved at. Everything this view
   * reads is covered by the ShellStore's events or signature (the
   * theater's stage changes bump it too), so a quiet frame skips the
   * whole resolve — the store views gate the same way. */
  private drawnVersion = -1;

  @on("render")
  onRender() {
    const version = this.game.entities.tryGetSingleton(ShellStore)?.version;
    if (version !== undefined && version === this.drawnVersion) return;
    this.drawnVersion = version ?? -1;

    // Undress last frame's targets…
    for (const undress of this.undressers) undress();
    this.undressers = [];

    const game = this.game;
    if (!game.entities.tryGetSingleton(Player)) return;
    const targeting = game.entities.tryGetSingleton(TargetingState);
    if (!targeting) return;
    const gs = interactFacts(game);
    if (gs.player.away) return;

    const targeted = targeting.targeted();
    const action = resolveInteract(
      gs,
      shopCellMap(this.game),
      targeted?.view(),
      targeting.pileOffset,
    );

    // …and dress this frame's. Exactly one thing wears E's rim: the
    // station when E would use it, the one piece when E would take it.
    // A machine worked from the floor keeps its rim regardless — Space
    // and the setting keys are aimed at it — so at a saw the rim sits
    // on the saw and on the offcut E would lift; at a bench, which is a
    // table out here, it moves between the bench and its stock.
    if (targeted) {
      const view = targeted.view();
      const usesStation =
        action?.kind === "open-station" ||
        action?.kind === "switch-on" ||
        action?.kind === "switch-off";
      if (usesStation || hasFloorControls(view.type)) {
        this.dress(viewHighlightRoot(targeted));
      }
    }

    // …the piece E would take off a machine (a bench's stock lies where
    // it can be pointed at; other machines fan theirs under one rim)…
    if (action?.kind === "take-inputs" || action?.kind === "take-outputs") {
      const entity = findMachineEntity(this.game, action.machine.state);
      const view = entity ? findChildView(entity, MachineView) : null;
      const piece = view?.pieceRoot(action.material) ?? null;
      if (piece) this.dress(piece);
      else if (entity) this.dress(viewHighlightRoot(entity));
    }

    // …the pile E would pick up…
    if (action?.kind === "pick-up-floor") {
      for (const entity of game.entities.byConstructor(MaterialPileEntity)) {
        if (entity.material.id === action.target.material.id) {
          this.dress(viewHighlightRoot(entity));
          break;
        }
      }
    }

    this.dressStand(gs, action);
    this.dressTruck(gs, action);
  }

  /**
   * The stand: the table lights when F would set work out on it, and the
   * piece E would take back lights on its own the way a floor pile does.
   */
  private dressStand(gs: InteractFacts, action: InteractAction | null): void {
    const stand = this.game.entities.tryGetSingleton(StandEntity);
    if (!stand) return;
    const atStandNow =
      gs.player.carriedMachine == null &&
      atStand(gs.shopInfo, gs.player.position);
    // The spot lights when E would set the table up there; the table,
    // once it's out, when F would set work on it.
    if (
      atStandNow &&
      (action?.kind === "set-up-stand" ||
        (stand.setUp && gs.player.inventory.some(isSellable)))
    ) {
      this.dress(viewHighlightRoot(stand));
    }
    if (action?.kind === "stand") {
      const view = findChildView(stand, StandView);
      this.dress(view?.pieceRoot(action.material) ?? null);
    }
  }

  /**
   * The truck, aimed at whatever the keys would actually move. Lifting a
   * piece back out is a pickup like any other, so the *piece* lights up,
   * not the truck. The cargo box itself only lights when the bed is the
   * thing being acted on — F loading what's in hand over the rail, or a
   * crate hoisting out of it — and the whole body lights when E would
   * open the cab.
   */
  private dressTruck(gs: InteractFacts, action: InteractAction | null): void {
    const truck = this.game.entities.tryGetSingleton(TruckEntity);
    if (!truck) return;
    const view = findChildView(truck, TruckView);
    const parked =
      (this.game.entities.tryGetSingleton(TripTheater)?.stage() ?? "parked") ===
      "parked";
    const atBed =
      parked &&
      gs.player.carriedMachine == null &&
      atTruckBed(gs.shopInfo, gs.player.position);
    const bedIsTheTarget =
      atBed && (gs.player.inventory.length > 0 || gs.truck.crates.length > 0);

    if (bedIsTheTarget) {
      this.dressHidden(view?.bedRimRoot ?? null);
    } else if (parked && action?.kind === "truck-cab") {
      this.dress(viewHighlightRoot(truck));
    }
    if (atBed && action?.kind === "truck-bed") {
      this.dress(view?.cargoRoot(action.material) ?? null);
    }
  }

  private dress(container: Container | null): void {
    if (!container || container.destroyed) return;
    container.filters = TARGET_HIGHLIGHT_FILTERS;
    this.undressers.push(() => {
      // The undress may run on the very bump that removed the thing.
      if (!container.destroyed) container.filters = [];
    });
  }

  /** A drawable that exists only to carry a rim — the truck's bed crop,
   * traced over the body art — so it shows exactly while it's dressed. */
  private dressHidden(container: Container | null): void {
    if (!container || container.destroyed) return;
    container.visible = true;
    container.filters = TARGET_HIGHLIGHT_FILTERS;
    this.undressers.push(() => {
      if (container.destroyed) return;
      container.visible = false;
      container.filters = [];
    });
  }
}
